import { htmlToText } from 'html-to-text'
import { useEffect, useState } from 'react'
import ReactDOMServer from 'react-dom/server'

import { PhnxAvatar } from '@/components/01-atoms/Avatar/Avatar'
import { PhnxButton } from '@/components/01-atoms/Button/Button'
import { PhnxShare } from '@/components/01-atoms/Share/Share'
import { PhnxAuthorcard } from '@/components/02-molecules/Authorcard/Authorcard'
import { PhnxHerobanner } from '@/components/02-molecules/Herobanner/Herobanner'
import { setDate } from '@/lib/date'

import CalendarIcon from '../../../../public/svg/calendar.svg'
import ReadIcon from '../../../../public/svg/flag.svg'
import styles from './Article.module.css'

export type ArticleProps = {
  featuredImage: { filename: string; alt: string; focus: string }
  cropImage?: boolean
  title: string
  h1: string
  tags: string[]
  author?: {
    title?: string
    name: string
    bio: string
    url?: string
    image: { src: string; alt: string }
  }
  date?: string
  children: React.ReactNode
  relatedArticles?: {
    image: { src: string; alt: string; focus: string }
    title: string
    tags: string[]
    date: string
    timeToRead: string
    link: string
  }[]
  relatedArticlesTitle?: string
  relatedArticlesCTA?: { label: string; link: string }
}

import { PhnxImageHolder } from '@/components/01-atoms/ImageHolder/ImageHolder'
import { PhnxCard } from '@/components/02-molecules/Card/Card'
import { PhnxRelatedArticles } from '@/components/03-organisms/Relatedarticles/RelatedArticles'
import { calculateReadingTime } from '@/lib/readingTime'

export const PhnxArticle: React.FC<ArticleProps> = ({
  featuredImage,
  cropImage,
  title,
  h1,
  tags,
  author,
  date,
  children,
  relatedArticlesTitle,
  relatedArticles,
  relatedArticlesCTA,
}) => {
  const [timetoread, setTimetoread] = useState('')

  useEffect(() => {
    const htmlString = ReactDOMServer.renderToStaticMarkup(<>{children}</>)

    const textContent = htmlToText(htmlString, { wordwrap: false })
    const readingTime = calculateReadingTime(textContent)
    setTimetoread(readingTime)
  }, [children])

  let dateFormatted = ''
  if (date) {
    dateFormatted = setDate(date)
  }

  return (
    <div className={styles['article']}>
      <div className={styles['feature-image']}>
        {featuredImage && (
          <PhnxHerobanner
            asset={{
              src: featuredImage.filename,
              alt: featuredImage.alt,
              focus: featuredImage.focus,
            }}
            layout="article"
            cropImage={cropImage}
          />
        )}
      </div>

      <div className={`${styles['article-container']} container`}>
        {tags && (
          <div className={styles.tags}>
            <ul>
              {tags.map((tag) => (
                <li key={tag}>{tag}</li>
              ))}
            </ul>
          </div>
        )}

        {title ? <h1 className={styles.title}>{title}</h1> : <h1 className={'sr-only'}>{h1}</h1>}

        <div className={styles.details}>
          {author && (
            <div className={`${styles.author} ${styles['details-container']}`}>
              <span className="inner">
                {author.image && (
                  <PhnxAvatar
                    size="small"
                    style="circle"
                    asset={{ src: author.image.src, alt: author.image.alt }}
                  />
                )}
              </span>
              {author.name && <div className="text">{author.name}</div>}
            </div>
          )}
          {date && (
            <div className={`${styles.date} ${styles['details-container']}`}>
              <span className="inner">
                <CalendarIcon />
              </span>
              <span className="text">{dateFormatted}</span>
            </div>
          )}
          <div className={`${styles.timetoread} ${styles['details-container']}`}>
            <span className="inner">
              <ReadIcon />
            </span>
            <span className="text">{timetoread}</span>
          </div>
          <div className={`${styles['details-container']}`}>
            <PhnxShare title={title} facebook linkedin email />
          </div>
        </div>

        <div className={styles.body} id="article-container">
          {children}
        </div>

        {author && (
          <div className={styles.authorcard}>
            <PhnxAuthorcard
              asset={{ alt: author.image.alt, src: author.image.src }}
              bio={author.bio}
              buttonURL={author.url}
              name={`Author: ${author.name} | ${author.title}`}
            />
          </div>
        )}

        {relatedArticles && relatedArticles.length > 0 && (
          <div className={styles['related-articles']}>
            <PhnxRelatedArticles
              button={
                <PhnxButton
                  label={relatedArticlesCTA?.label}
                  type="link"
                  href={relatedArticlesCTA?.link}
                  variant="tertiary"
                />
              }
              items={relatedArticles.map((article) => (
                <PhnxCard
                  key={`${article.title}-${article.date}`}
                  link={article.link}
                  alignment="default"
                  asset={
                    article.image && (
                      <PhnxImageHolder
                        images={[
                          {
                            alt: article.image.alt,
                            focus: article.image.focus,
                            src: article.image.src,
                          },
                        ]}
                      />
                    )
                  }
                  button={<PhnxButton label="Read Story" type="button" variant="quinary" />}
                  subtitle={
                    <div>
                      <div className="date">{article.date}</div>
                      <div className="timetoread">{article.timeToRead}</div>
                    </div>
                  }
                  tags={article.tags}
                  layout="vertical"
                  style="square"
                  title={article.title}
                  variant="senary"
                />
              ))}
              layout="secondary"
              subtitle=""
              title={relatedArticlesTitle}
            />
          </div>
        )}
      </div>
    </div>
  )
}
